/**
 * Symphony workspace ("Spaces") avatar — the shared 5-style initials fallback.
 *
 * When a workspace has no uploaded icon, its badge falls back to a deterministic initials tile: one
 * of 5 fixed styles chosen by the workspace's STABLE id, so the same workspace reads identically on
 * every surface (the drawer, the home list, the workspace header, the piled-up card) and on every
 * platform. Name drifts on rename, team position drifts on reorder; only the id is stable.
 *
 * The palette is Figma's `Workspace/Workspace NN` group (Figma "spaces nav", node `11400:41580`) —
 * the same hues as the agent dots, which is why this lives next to `agentColor.ts` and reuses its
 * canonical hash. The colors are a generated cross-platform data contract, not a styling choice, so
 * they are literals here exactly as `AGENT_OPTIONS` is; consumers read them from this module rather
 * than writing a hex of their own.
 *
 * Byte-identical to the native ports (`WorkspaceAvatar.swift` / `WorkspaceAvatar.kt`): same style
 * list in index order, and the same `mix32(javaHash(id)) % 5` mapping. Native bakes each style's
 * background + ring into a pre-generated asset per shape; web draws the tile in CSS from the same
 * triple. `native-apps/DESIGN_SPEC.md` → "Shared workspace avatar" is the spec both sides follow.
 */

import { javaHash, mix32 } from '../Agent/agentColor';

/** One of the 5 fixed workspace-avatar styles: a background / border-ring / initials color triple. */

/**
 * The 5 styles, index 0..4 in Figma "Spaces" order. DO NOT re-order — the index is what the id hash
 * selects, and a re-order silently repaints every existing workspace on all three surfaces.
 */
export const WORKSPACE_AVATAR_STYLES = [{
  index: 0,
  background: '#CCE38C',
  ring: '#D6FF59',
  initials: '#565D33'
}, {
  index: 1,
  background: '#EDCBFF',
  ring: '#9CD5C6',
  initials: '#FF4E00'
}, {
  index: 2,
  background: '#9CD5C6',
  ring: '#276454',
  initials: '#D6FF59'
}, {
  index: 3,
  background: '#3B6BE1',
  ring: '#FFC738',
  initials: '#EDCBFF'
}, {
  index: 4,
  background: '#FFC738',
  ring: '#FF9FFC',
  initials: '#565D33'
}];

/**
 * The silhouette a workspace avatar is drawn in: a rounded square everywhere (drawer, home list,
 * workspace header) except the piled-up home component, which uses a circle so an unassigned job's
 * workspace badge reads as the same shape as the agent dot it stands in for. Mirrors iOS/Android
 * `WorkspaceAvatarShape`.
 */

/** Corner radius as a fraction of the tile's side (Figma "Spaces"). */
export const WORKSPACE_AVATAR_RADIUS_RATIO = 0.3;
/** Inside border-ring width as a fraction of the side, rounded-square (Figma "Spaces"). */
export const WORKSPACE_AVATAR_RING_RATIO = 0.08;
/**
 * Inside border-ring width as a fraction of the side, CIRCLE — a much finer ring than the square's.
 * Taken from the native circle asset's own geometry (`workspace_avatar_circle_N.xml`:
 * strokeWidth 2.2617 on a 68.0723 viewport), which is where both platforms get it.
 */
export const WORKSPACE_AVATAR_CIRCLE_RING_RATIO = 2.2617 / 68.0723;
/** Initials font size as a fraction of the tile's side (Figma "Spaces"). */
export const WORKSPACE_AVATAR_INITIALS_RATIO = 0.4;

/** The chosen style index (0..4) for a workspace's stable id — same result on web/iOS/Android. */
export function workspaceAvatarStyleIndex(id) {
  return mix32(javaHash(id)) % WORKSPACE_AVATAR_STYLES.length;
}

/** The style chosen for a workspace's stable id. */
export function workspaceAvatarStyle(id) {
  return WORKSPACE_AVATAR_STYLES[workspaceAvatarStyleIndex(id)];
}

/**
 * The 2-letter initials for a workspace name: spaces stripped, first two characters, uppercased.
 * Shared 1:1 with `WorkspaceAvatar.initials` (iOS) / `workspaceInitials` (Android).
 */
export function workspaceInitials(name) {
  return name.replace(/ /g, '').slice(0, 2).toUpperCase();
}
//# sourceMappingURL=workspaceAvatar.js.map